// src/pages/FlightSheetDashboard.js - Updated
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Badge,
    Spinner,
    Alert,
    Button,
    Tabs,
    Tab,
    ButtonGroup,
    ToggleButton
} from 'react-bootstrap';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { io } from 'socket.io-client';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PencilSquare, ArrowClockwise, ArrowLeft } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import CopyButton from '../components/CopyButton';

// Добавляем CSS стили для скрытия круглых индикаторов радиокнопок
const toggleButtonStyles = `
  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const FlightSheetDashboard = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const { t } = useTranslation();

    const [flightSheet, setFlightSheet] = useState(null);
    const [stats, setStats] = useState(null);
    const [workers, setWorkers] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [socket, setSocket] = useState(null);
    const [timeRange, setTimeRange] = useState('24h');
    const [intervalPeriod, setIntervalPeriod] = useState('10m');
    const [stratums, setStratums] = useState([]);
    const [hourlyHashrate, setHourlyHashrate] = useState('0 H/s');
    const [dailyHashrate, setDailyHashrate] = useState('0 H/s');

    // Function to fetch historical data with time range
    const fetchHistoricalData = useCallback(async (period, interval) => {
        try {
            setRefreshing(true);
            console.log(`Fetching historical data with period=${period}, interval=${interval}`);
            const historyResponse = await api.get(`/api/stats/history/${id}?period=${period}&interval=${interval}`);
            setHistory(historyResponse.data.data.history);
            setRefreshing(false);
            return historyResponse.data.data.history;
        } catch (err) {
            console.error('Error fetching historical data:', err);
            setRefreshing(false);
            return [];
        }
    }, [id]);

    // Fetch stratum addresses from the backend
    const fetchStratumAddresses = useCallback(async () => {
        try {
            // Try to get stratum addresses from the server config
            const configResponse = await api.get('/api/config/stratums');
            if (configResponse.data && configResponse.data.success) {
                setStratums(configResponse.data.data || []);
            } else {
                // If not available, set a default value
                setStratums(['stratum+tcp://proxy.hashcontrol.online:3333']);
            }
        } catch (err) {
            console.error('Error fetching stratum addresses:', err);
            // Set default value if fetch fails
            setStratums(['stratum+tcp://proxy.hashcontrol.online:3333']);
        }
    }, []);

    // Fetch flight sheet data
    useEffect(() => {
        const fetchFlightSheet = async () => {
            try {
                // Get flight sheet data
                const flightSheetResponse = await api.get(`/api/flight-sheets/${id}`);
                setFlightSheet(flightSheetResponse.data.data);

                // Get statistics
                const statsResponse = await api.get(`/api/stats/flight-sheet/${id}`);
                setStats(statsResponse.data.data.stats);
                setWorkers(statsResponse.data.data.workers);

                // Fetch stratum addresses
                await fetchStratumAddresses();

                // Calculate hourly and daily hashrates (ideally this should come from backend)
                calculateAverageHashrates(statsResponse.data.data.workers);

                setLoading(false);
            } catch (err) {
                setError(
                    err.response?.data?.message ||
                    t('errors.loadingError')
                );
                setLoading(false);
            }
        };

        fetchFlightSheet();

        // Initial load of historical data
        fetchHistoricalData(timeRange, intervalPeriod);
    }, [id, fetchStratumAddresses, fetchHistoricalData, t]);

    // Calculate average hashrates for different time periods
    const calculateAverageHashrates = (workersData) => {
        // This is a placeholder. In a real implementation, the backend should provide this data
        // or we should calculate it from historical data
        const totalHashrate = workersData.reduce((sum, w) => sum + (w.hashrate || 0), 0);

        // For demo purposes, we'll just set some values
        setHourlyHashrate(formatHashrate(totalHashrate * 0.95)); // Slightly lower than current
        setDailyHashrate(formatHashrate(totalHashrate * 0.85)); // Lower than hourly
    };

    // WebSocket connection
    useEffect(() => {
        if (!token) return;

        // Create connection
        const socketIo = io(process.env.REACT_APP_API_URL || 'http://localhost:5000', {
            withCredentials: true
        });

        // Authenticate socket
        socketIo.on('connect', () => {
            console.log('Socket connected');
            socketIo.emit('authenticate', token);
        });

        // Handle authentication success
        socketIo.on('authenticated', (data) => {
            if (data.success) {
                console.log('Socket authenticated');
            } else {
                console.error('Socket authentication failed');
            }
        });

        // Handle miner updates
        socketIo.on('miner_update', (data) => {
            if (data.flightSheetId === id) {
                // Update worker data
                setWorkers(prev => {
                    const newWorkers = [...prev];
                    const index = newWorkers.findIndex(w => w.minerWorker === data.minerWorker);

                    if (index !== -1) {
                        newWorkers[index] = {
                            ...newWorkers[index],
                            hashrate: data.hashrate,
                            hashrateFormatted: data.hashrateFormatted,
                            acceptedShares: (newWorkers[index].acceptedShares || 0) + (data.share?.accepted ? 1 : 0),
                            rejectedShares: (newWorkers[index].rejectedShares || 0) + (data.share?.accepted ? 0 : 1)
                        };
                    }

                    return newWorkers;
                });

                // Update overall statistics
                setStats(prev => {
                    if (!prev) return prev;

                    // Calculate total hashrate from all workers
                    const totalHashrate = workers.reduce((sum, w) => sum + (w.hashrate || 0), 0);

                    return {
                        ...prev,
                        totalHashrate: totalHashrate,
                        hashrateFormatted: formatHashrate(totalHashrate),
                        acceptedShares: prev.acceptedShares + (data.share?.accepted ? 1 : 0),
                        rejectedShares: prev.rejectedShares + (data.share?.accepted ? 0 : 1),
                        totalShares: prev.totalShares + 1
                    };
                });

                // Update average hashrates
                calculateAverageHashrates(workers);
            }
        });

        // Handle new miner connections
        socketIo.on('miner_connected', (data) => {
            if (data.flightSheetId === id) {
                console.log('New miner connected', data);

                // Update workers list
                setWorkers(prev => {
                    const index = prev.findIndex(w => w.minerWorker === data.minerWorker);

                    if (index !== -1) {
                        const updatedWorkers = [...prev];
                        updatedWorkers[index] = {
                            ...updatedWorkers[index],
                            isConnected: true,
                            ip: data.ip
                        };
                        return updatedWorkers;
                    } else {
                        return [...prev, {
                            minerWorker: data.minerWorker,
                            ip: data.ip,
                            isConnected: true,
                            hashrate: 0,
                            hashrateFormatted: '0 H/s',
                            acceptedShares: 0,
                            rejectedShares: 0
                        }];
                    }
                });

                // Update statistics
                setStats(prev => {
                    if (!prev) return prev;

                    return {
                        ...prev,
                        activeWorkers: prev.activeWorkers + 1
                    };
                });
            }
        });

        // Handle miner disconnections
        socketIo.on('miner_disconnected', (data) => {
            if (data.flightSheetId === id) {
                console.log('Miner disconnected', data);

                // Update workers list
                setWorkers(prev => {
                    const updatedWorkers = [...prev];
                    const index = updatedWorkers.findIndex(w => w.minerWorker === data.minerWorker);

                    if (index !== -1) {
                        updatedWorkers[index] = {
                            ...updatedWorkers[index],
                            isConnected: false
                        };
                    }

                    return updatedWorkers;
                });

                // Update statistics
                setStats(prev => {
                    if (!prev) return prev;

                    return {
                        ...prev,
                        activeWorkers: Math.max(0, prev.activeWorkers - 1)
                    };
                });
            }
        });

        setSocket(socketIo);

        // Disconnect socket when component unmounts
        return () => {
            if (socketIo) {
                socketIo.disconnect();
            }
        };
    }, [token, id, workers]);

    // Handle data refresh
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            // Get statistics
            const statsResponse = await api.get(`/api/stats/flight-sheet/${id}`);
            setStats(statsResponse.data.data.stats);
            setWorkers(statsResponse.data.data.workers);

            // Get history with current time range and interval
            await fetchHistoricalData(timeRange, intervalPeriod);

            // Calculate average hashrates
            calculateAverageHashrates(statsResponse.data.data.workers);

            setRefreshing(false);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                t('errors.updateError')
            );
            setRefreshing(false);
        }
    };

    // Handle time range change
    const handleTimeRangeChange = async (range, interval) => {
        console.log(`Changing time range to: ${range}, interval: ${interval}`);
        setTimeRange(range);
        setIntervalPeriod(interval);
        await fetchHistoricalData(range, interval);
    };

    // Format hashrate
    const formatHashrate = (hashrate) => {
        if (!hashrate || hashrate < 0) return '0 H/s';

        if (hashrate < 1000) {
            return `${hashrate.toFixed(2)} H/s`;
        } else if (hashrate < 1000000) {
            return `${(hashrate / 1000).toFixed(2)} KH/s`;
        } else if (hashrate < 1000000000) {
            return `${(hashrate / 1000000).toFixed(2)} MH/s`;
        } else if (hashrate < 1000000000000) {
            return `${(hashrate / 1000000000).toFixed(2)} GH/s`;
        } else {
            return `${(hashrate / 1000000000000).toFixed(2)} TH/s`;
        }
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString();
    };

    // Count online and offline workers
    const getWorkerCountString = () => {
        if (!workers || workers.length === 0) return '0/0';

        const online = workers.filter(w => w.isConnected).length;
        const offline = workers.length - online;
        return `${online}/${offline}`;
    };

    if (loading) {
        return (
            <Container className="my-5 text-center">
                <Spinner animation="border" variant="primary"/>
                <p className="mt-3">{t('common.loading')}</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="my-5">
                <Alert variant="danger">
                    <Alert.Heading>{t('common.error')}</Alert.Heading>
                    <p>{error}</p>
                    <hr/>
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="outline-danger"
                            onClick={() => navigate('/dashboard')}
                        >
                            {t('admin.returnToDashboard')}
                        </Button>
                    </div>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{flightSheet?.name || t('flightSheet.details')}</h1>
                <div>
                    <Button
                        variant="outline-primary"
                        className="me-2"
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        {refreshing ? (
                            <Spinner animation="border" size="sm"/>
                        ) : (
                            <><ArrowClockwise className="me-1"/> {t('common.refresh')}</>
                        )}
                    </Button>
                    <Button
                        variant="outline-success"
                        className="me-2"
                        onClick={() => navigate(`/flight-sheets/${id}/edit`)}
                    >
                        <PencilSquare className="me-1"/> {t('common.edit')}
                    </Button>
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/dashboard')}
                    >
                        <ArrowLeft className="me-1"/> {t('common.back')}
                    </Button>
                </div>
            </div>

            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Row>
                        <Col md={3} className="border-end">
                            <div className="text-center mb-3">
                                <h5>{t('dashboard.currentHashrate')}</h5>
                                <h2 className="text-primary">{stats?.hashrateFormatted || '0 H/s'}</h2>
                            </div>
                        </Col>
                        <Col md={3} className="border-end">
                            <div className="text-center mb-3">
                                <h5>{t('dashboard.workers')}</h5>
                                <h2>{getWorkerCountString()}</h2>
                            </div>
                        </Col>
                        <Col md={3} className="border-end">
                            <div className="text-center mb-3">
                                <h5>{t('dashboard.shares')}</h5>
                                <h2>{stats?.totalShares || 0}</h2>
                                <div className="d-flex justify-content-center gap-2">
                                    <Badge bg="success" pill>{stats?.acceptedShares || 0}</Badge>
                                    <Badge bg="danger" pill>{stats?.rejectedShares || 0}</Badge>
                                </div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="text-center mb-3">
                                <h5>{t('dashboard.acceptanceRate')}</h5>
                                <h2>{stats?.acceptanceRate || '0%'}</h2>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
            >
                <Tab eventKey="overview" title={t('admin.overview')}>
                    <Row className="mb-4">
                        <Col md={4}>
                            <Card className="shadow-sm h-100">
                                <Card.Header>
                                    <h5 className="mb-0">{t('flightSheet.info')}</h5>
                                </Card.Header>
                                <Card.Body className="info-section">
                                    <p>
                                        <strong>{t('flightSheet.stratums')}:</strong>
                                        {stratums.length > 0 ? (
                                            stratums.map((address, index) => (
                                                <div key={index} className="value-container">
                                                    <div className="stratum-address">{address}</div>
                                                    <CopyButton text={address} />
                                                </div>
                                            ))
                                        ) : (
                                            <div className="value-container">
                                                <div className="stratum-address">stratum+tcp://proxy.hashcontrol.online:3333</div>
                                                <CopyButton text="stratum+tcp://proxy.hashcontrol.online:3333" />
                                            </div>
                                        )}
                                    </p>
                                    <p>
                                        <strong>{t('flightSheet.minerId')}:</strong>
                                        <div className="value-container">
                                            <span className="minerWorker-id">{flightSheet?.minerWorker || ''}</span>
                                            <CopyButton text={flightSheet?.minerWorker || ''} />
                                        </div>
                                        <small className="d-block text-muted mt-1">
                                            {t('flightSheet.useMinerID')}
                                        </small>
                                    </p>
                                    <p>
                                        <strong>Pool:</strong>
                                        <span>{flightSheet?.poolHost || ''}:{flightSheet?.poolPort || ''}</span>
                                    </p>
                                    <p>
                                        <strong>{t('flightSheet.poolWorker')}:</strong>
                                        <span>{flightSheet?.poolWorker || ''}</span>
                                    </p>
                                    <p className="mb-0">
                                        <strong>{t('flightSheet.createdAt')}:</strong>
                                        <span>
        {flightSheet?.createdAt
            ? new Date(flightSheet.createdAt).toLocaleString()
            : ''}
      </span>
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={8}>
                            <Card className="shadow-sm h-100">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">{t('charts.hashrateChart')}</h5>
                                    {/* Добавляем стили для скрытия радиокнопок */}
                                    <style>
                                        {toggleButtonStyles}
                                    </style>
                                    <ButtonGroup>
                                        <ToggleButton
                                            id="radio-10m"
                                            type="radio"
                                            variant="outline-primary"
                                            name="timeRange"
                                            value="10m"
                                            checked={timeRange === '10m' && intervalPeriod === '1m'}
                                            onChange={() => handleTimeRangeChange('10m', '1m')}
                                            size="sm"
                                        >
                                            10 {t('charts.minutes')}
                                        </ToggleButton>
                                        <ToggleButton
                                            id="radio-1h"
                                            type="radio"
                                            variant="outline-primary"
                                            name="timeRange"
                                            value="1h"
                                            checked={timeRange === '1h' && intervalPeriod === '5m'}
                                            onChange={() => handleTimeRangeChange('1h', '5m')}
                                            size="sm"
                                        >
                                            1 {t('charts.hour')}
                                        </ToggleButton>
                                        <ToggleButton
                                            id="radio-24h"
                                            type="radio"
                                            variant="outline-primary"
                                            name="timeRange"
                                            value="24h"
                                            checked={timeRange === '24h' && intervalPeriod === '10m'}
                                            onChange={() => handleTimeRangeChange('24h', '10m')}
                                            size="sm"
                                        >
                                            24 {t('charts.hours')}
                                        </ToggleButton>
                                    </ButtonGroup>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <div className="text-center">
                                                <h6 className="text-muted">{t('charts.current')}</h6>
                                                <h4 className="text-primary">{stats?.hashrateFormatted || '0 H/s'}</h4>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="text-center">
                                                <h6 className="text-muted">{t('charts.hourly')}</h6>
                                                <h4 className="text-primary">{hourlyHashrate}</h4>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="text-center">
                                                <h6 className="text-muted">{t('charts.daily')}</h6>
                                                <h4 className="text-primary">{dailyHashrate}</h4>
                                            </div>
                                        </Col>
                                    </Row>

                                    {refreshing ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary"/>
                                            <p className="mt-3">{t('common.loading')}</p>
                                        </div>
                                    ) : history.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <AreaChart
                                                data={history}
                                                margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                            >
                                                <CartesianGrid strokeDasharray="3 3"/>
                                                <XAxis
                                                    dataKey="timestamp"
                                                    tickFormatter={formatDate}
                                                    interval="preserveEnd"
                                                />
                                                {/* Исправлено форматирование оси Y */}
                                                <YAxis
                                                    tickFormatter={(value) => {
                                                        if (value < 1000) return value;
                                                        if (value < 1000000) return `${(value / 1000).toFixed(0)}K`;
                                                        if (value < 1000000000) return `${(value / 1000000).toFixed(0)}M`;
                                                        if (value < 1000000000000) return `${(value / 1000000000).toFixed(0)}G`;
                                                        return `${(value / 1000000000000).toFixed(0)}T`;
                                                    }}
                                                    label={{
                                                        value: 'H/s',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                        style: {fill: 'rgba(255,255,255,0.5)'}
                                                    }}
                                                />
                                                <Tooltip
                                                    formatter={(value) => [formatHashrate(value), t('charts.hashrate')]}
                                                    labelFormatter={formatDate}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="hashrate"
                                                    stroke="#8884d8"
                                                    fill="#8884d8"
                                                    fillOpacity={0.3}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="text-center py-5">
                                            <p className="text-muted">{t('charts.noData')}</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="shadow-sm mb-4">
                        <Card.Header>
                            <h5 className="mb-0">{t('dashboard.workers')}</h5>
                        </Card.Header>
                        <Card.Body>
                            {workers.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead>
                                        <tr>
                                            <th>{t('dashboard.workers')}</th>
                                            <th>IP</th>
                                            <th>{t('common.status')}</th>
                                            <th>{t('dashboard.hashrate')}</th>
                                            <th>Difficulty</th>
                                            <th>{t('dashboard.accepted')}</th>
                                            <th>{t('dashboard.rejected')}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {workers.map(worker => (
                                            <tr key={worker.minerWorker}>
                                                <td>{worker.minerWorker}</td>
                                                <td>{worker.ip}</td>
                                                <td>
                                                    {worker.isConnected ? (
                                                        <Badge bg="success">{t('common.online')}</Badge>
                                                    ) : (
                                                        <Badge bg="danger">{t('common.offline')}</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <strong className="text-primary">
                                                        {worker.hashrateFormatted || '0 H/s'}
                                                    </strong>
                                                </td>
                                                <td>{worker.avgDifficulty?.toFixed(2) || '1.00'}</td>
                                                <td className="text-success">{worker.acceptedShares || 0}</td>
                                                <td className="text-danger">{worker.rejectedShares || 0}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className="mb-0 text-muted">{t('charts.noData')}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">{t('dashboard.shares')}</h5>
                        </Card.Header>
                        <Card.Body>
                            {refreshing ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary"/>
                                    <p className="mt-3">{t('common.loading')}</p>
                                </div>
                            ) : history.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart
                                        data={history}
                                        margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                    >
                                        <CartesianGrid strokeDasharray="3 3"/>
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatDate}
                                            interval="preserveEnd"
                                        />
                                        <YAxis/>
                                        <Tooltip
                                            formatter={(value, name) => [value, name === 'acceptedShares' ? t('dashboard.accepted') : t('dashboard.rejected')]}
                                            labelFormatter={formatDate}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="acceptedShares"
                                            name={t('dashboard.accepted')}
                                            stroke="#28a745"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="rejectedShares"
                                            name={t('dashboard.rejected')}
                                            stroke="#dc3545"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-muted">{t('charts.noData')}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </Container>
    );
};

export default FlightSheetDashboard;